import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewsV2Endpoints } from '@/lib/api/endpoints';
import { apiPatch, apiPost, operationsClient } from '@/lib/api';
import type { AddInternalNoteDto, FlagReviewDto, ReviewResponseDetail } from '@/types/review';
import { invalidateReviewViews } from './invalidateReviewViews';

type DetailEnvelope = { data: ReviewResponseDetail };

/**
 * Append an internal note to a `ReviewResponse` row
 * (`PATCH /v2/reviews/response/:id/internal-note`).
 */
export function useAddInternalNote() {
  const queryClient = useQueryClient();

  return useApiMutation<DetailEnvelope, { reviewResponseId: string; dto: AddInternalNoteDto }>({
    mutationFn: ({ reviewResponseId, dto }) =>
      apiPatch<DetailEnvelope, AddInternalNoteDto>(
        operationsClient,
        reviewsV2Endpoints.internalNote(reviewResponseId),
        dto,
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('response', variables.reviewResponseId),
      });
      invalidateReviewViews(queryClient);
      toast.success('Note added');
    },
  });
}

/**
 * Flag a `ReviewResponse` for owner/manager escalation
 * (`POST /v2/reviews/response/:id/flag`).
 *
 * The backend stores the flag on `sentimentAnalysis.escalation` until a
 * dedicated column ships — see `ReviewResponseService` header.
 */
export function useFlagReview() {
  const queryClient = useQueryClient();

  return useApiMutation<DetailEnvelope, { reviewResponseId: string; dto: FlagReviewDto }>({
    mutationFn: ({ reviewResponseId, dto }) =>
      apiPost<DetailEnvelope, FlagReviewDto>(
        operationsClient,
        reviewsV2Endpoints.flag(reviewResponseId),
        dto,
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('response', variables.reviewResponseId),
      });
      invalidateReviewViews(queryClient);
      toast.success('Review flagged for escalation');
    },
  });
}

/**
 * Publish or unpublish a testimonial on the public job-stories page
 * (`PATCH /v2/reviews/testimonial/:id/approval`).
 *
 * Gated on `reviews:approve`, not `reviews:edit` — the caller is responsible
 * for hiding the control, but the backend refuses it either way.
 */
export function useSetTestimonialApproval() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { data: { id: string; isApprovedForDisplay: boolean } },
    { testimonialId: string; approved: boolean }
  >({
    mutationFn: ({ testimonialId, approved }) =>
      apiPatch<{ data: { id: string; isApprovedForDisplay: boolean } }, { approved: boolean }>(
        operationsClient,
        reviewsV2Endpoints.testimonialApproval(testimonialId),
        { approved },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('testimonial', variables.testimonialId),
      });
      invalidateReviewViews(queryClient);
      toast.success(
        variables.approved
          ? 'Testimonial published to your public page'
          : 'Testimonial removed from your public page',
      );
    },
  });
}

/**
 * Clear a consented review response for the public page, or take it back
 * (`PATCH /v2/reviews/response/:id/approval`).
 *
 * Approval is only half the gate: the public read also requires the
 * customer's `publishConsent`, which no staff action can supply. Approving a
 * response nobody consented to publishes nothing, by design.
 */
export function useSetResponseApproval() {
  const queryClient = useQueryClient();

  return useApiMutation<
    { data: { id: string; isApprovedForDisplay: boolean } },
    { reviewResponseId: string; approved: boolean }
  >({
    mutationFn: ({ reviewResponseId, approved }) =>
      apiPatch<{ data: { id: string; isApprovedForDisplay: boolean } }, { approved: boolean }>(
        operationsClient,
        reviewsV2Endpoints.responseApproval(reviewResponseId),
        { approved },
      ),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('response', variables.reviewResponseId),
      });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.lists() });
      toast.success(
        variables.approved
          ? 'Review published to your public page'
          : 'Review removed from your public page',
      );
    },
  });
}

export function useLinkExternalReviewAppointment() {
  const queryClient = useQueryClient();

  return useApiMutation<DetailEnvelope, { externalReviewId: string; appointmentId: string | null }>(
    {
      mutationFn: ({ externalReviewId, appointmentId }) =>
        apiPatch<DetailEnvelope, { appointmentId: string | null }>(
          operationsClient,
          reviewsV2Endpoints.linkExternalAppointment(externalReviewId),
          { appointmentId },
        ),
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries({
          queryKey: reviewKeys.detail('external', variables.externalReviewId),
        });
        invalidateReviewViews(queryClient);
        toast.success(variables.appointmentId ? 'Review linked to visit' : 'Visit link removed');
      },
    },
  );
}
